import { useState, useMemo } from "react";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, Euro, Hammer, TrendingUp, Award, Banknote, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  computeSteps, getSecondProgramSteps,
  TIMELINE_174_STEPS, MPR_TIMELINE_STEPS,
  GESTES_ISO_LABELS, COUT_UNITAIRE,
  buildCashFlowEvents
} from "@/utils/simulateurUtils";
import { isRestrictedUser } from "../Login";

const fc = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
const PRECARITE_LABELS = { bleu: "Bleu (Tres modeste)", jaune: "Jaune (Modeste)", violet: "Violet (Intermediaire)", rose: "Rose (Superieur)" };

// Render timeline inline (simplified version)
function renderTimelineInline(timelines, totalDays) {
  if (!timelines || timelines.length === 0) return null;
  return (
    <div className="space-y-4">
      {timelines.map((tl, ti) => (
        <div key={ti}>
          <h5 className={cn("text-xs font-semibold mb-2", tl.color === "emerald" ? "text-emerald-700" : tl.color === "blue" ? "text-blue-700" : "text-violet-700")}>{tl.label}</h5>
          <div className="space-y-1">
            {(tl.steps || []).map((step, si) => {
              const pct = totalDays > 0 ? (step.day / totalDays) * 100 : 0;
              return (
                <div key={si} className="flex items-center gap-2 text-xs">
                  <div className={cn("w-2 h-2 rounded-full shrink-0", tl.color === "emerald" ? "bg-emerald-500" : tl.color === "blue" ? "bg-blue-500" : "bg-violet-500")}></div>
                  <span className="text-muted-foreground w-12 shrink-0">J+{step.day}</span>
                  <span className="font-medium">{step.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SimulationResultsTab({ simulationData }) {
  const sd = simulationData;
  const [show174Detail, setShow174Detail] = useState(false);
  const [showMPRDetail, setShowMPRDetail] = useState(false);
  const isRestricted = isRestrictedUser();

  // Timeline computation - MUST be before early return for hooks rules
  const timelines = useMemo(() => {
    if (!sd) return { timelines: [], cashEvents: [], totalDays: 0 };
    
    const programme = sd.programme_selectionne || "174";
    const isCum = programme === "cumule";
    const multiN = sd.multiN || 1;
    const active174 = sd.active174Result ? { result: sd.active174Result } : null;
    const activeMPR = sd.activeMPRResult ? { result: sd.activeMPRResult, type: sd.activeMPRType } : null;
    const active174Cum = sd.active174CumResult ? { result: sd.active174CumResult } : null;
    const activeMPRCum = sd.activeMPRCumResult ? { result: sd.activeMPRCumResult } : null;
    const regies = sd.regies || {};
    const regieMode = sd.regieMode || "regie";
    
    const today = new Date();
    const elig174 = sd.elig174 || {};
    const eligMPR = sd.eligMPR || {};
    const eligCumule = sd.eligCumule || {};
    let tl = [];

    try {
      if (isCum && eligCumule.order) {
        const firstRaw = eligCumule.order[0] === "MPR" ? MPR_TIMELINE_STEPS : TIMELINE_174_STEPS;
        const secondRaw = eligCumule.order[0] === "MPR" ? TIMELINE_174_STEPS : MPR_TIMELINE_STEPS;
        const firstSteps = computeSteps(firstRaw, today);
        const firstEnd = firstSteps[firstSteps.length - 1]?.day || 0;
        const secondFiltered = getSecondProgramSteps(secondRaw);
        const secondStart = new Date(today);
        secondStart.setDate(secondStart.getDate() + firstEnd);
        const secondSteps = computeSteps(secondFiltered, secondStart);
        secondSteps.forEach(s => s.day += firstEnd);
        tl = [
          { label: eligCumule.order[0] === "MPR" ? "Programme MPR" : "Programme 174", steps: firstSteps, color: eligCumule.order[0] === "MPR" ? "blue" : "emerald" },
          { label: eligCumule.order[0] === "MPR" ? "Programme 174" : "Programme MPR", steps: secondSteps, color: eligCumule.order[0] === "MPR" ? "emerald" : "blue" }
        ];
      } else if (programme === "174" && elig174.eligible) {
        tl = [{ label: "Programme 174", steps: computeSteps(TIMELINE_174_STEPS, today), color: "emerald" }];
      } else if (programme === "mpr" && eligMPR.eligible) {
        tl = [{ label: "Programme MPR", steps: computeSteps(MPR_TIMELINE_STEPS, today), color: "blue" }];
      }
    } catch (e) {
    }

    if (tl.length === 0) return { timelines: [], cashEvents: [], totalDays: 0 };
    const allSteps = tl.flatMap(t => t.steps);
    const totalDays = Math.max(...allSteps.map(s => s.day));

    let cashEvents = [];
    try {
      if (isCum && sd.eligCumule?.order) {
        const firstIsMPR = sd.eligCumule.order[0] === "MPR";
        const firstEnd = tl[0].steps[tl[0].steps.length - 1]?.day || 0;
        if (firstIsMPR) {
          cashEvents = [
            ...buildCashFlowEvents("mpr", today, 0, activeMPRCum || activeMPR, null, regies, regieMode, true, false),
            ...buildCashFlowEvents("174", today, firstEnd, null, active174Cum || active174, regies, regieMode, true, true)
          ];
        } else {
          cashEvents = [
            ...buildCashFlowEvents("174", today, 0, null, active174Cum || active174, regies, regieMode, true, false),
            ...buildCashFlowEvents("mpr", today, firstEnd, activeMPRCum || activeMPR, null, regies, regieMode, true, true)
          ];
        }
      } else if (programme === "174" && active174) {
        cashEvents = buildCashFlowEvents("174", today, 0, null, active174, regies, regieMode, false, false);
      } else if (programme === "mpr" && activeMPR) {
        cashEvents = buildCashFlowEvents("mpr", today, 0, activeMPR, null, regies, regieMode, false, false);
      }
      cashEvents.sort((a, b) => a.day - b.day);
      let balance = 0;
      cashEvents = cashEvents.map(e => {
        balance += e.amount;
        const d = new Date(today); d.setDate(d.getDate() + e.day);
        return { ...e, balance, dateStr: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) };
      });
    } catch (e) {
    }

    return { timelines: tl, cashEvents, totalDays };
  }, [sd]);

  if (!sd) return null;

  const programme = sd.programme_selectionne || "174";
  const isCum = programme === "cumule";
  const multiN = sd.multiN || 1;
  const recommendation = sd.recommendation || [];
  const active174 = sd.active174Result ? { result: sd.active174Result } : null;
  const activeMPR = sd.activeMPRResult ? { result: sd.activeMPRResult, type: sd.activeMPRType } : null;
  const active174Cum = sd.active174CumResult ? { result: sd.active174CumResult } : null;
  const activeMPRCum = sd.activeMPRCumResult ? { result: sd.activeMPRCumResult } : null;
  const results174 = sd.results174 || [];
  const allMPRResults = sd.allMPRResults || [];

  return (
    <div className="space-y-5">
      {/* Recommendation */}
      {recommendation.length > 0 && (
        <div className="border border-indigo-200 rounded-lg bg-indigo-500/10 p-4">
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-indigo-600" /> Recommandation</h4>
          <div className="space-y-1">
            {recommendation.map((r, i) => (
              <div key={i} className={cn("text-sm px-3 py-1.5 rounded-lg", i === 0 ? "bg-indigo-100 font-semibold text-indigo-800" : "text-indigo-600")}>
                {i === 0 && <span className="text-xs mr-2">Le plus rentable</span>}
                {r.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profil et Logement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <h4 className="font-semibold text-sm mb-3 text-indigo-700">Profil client</h4>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Profil</span><span className="font-medium">{sd.profil}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Type batiment</span><span className="font-medium">{sd.typeBatiment}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Zone</span><span className="font-medium">{sd.zone}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Surface</span><span className="font-medium">{sd.surface} m2</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Chauffage</span><span className="font-medium">{sd.chauffage}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Niveaux</span><span className="font-medium">{sd.niveaux}</span></div>
          </div>
        </div>
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <h4 className="font-semibold text-sm mb-3 text-indigo-700">DPE & Precarite</h4>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">DPE actuel</span><span className="font-medium">{sd.dpeActuel}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Age du logement</span><span className="font-medium">{sd.ageLogement}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Precarite</span><span className="font-medium">{PRECARITE_LABELS[sd.precarite] || sd.precarite}</span></div>
            {sd.nbPersonnes && <div className="flex justify-between"><span className="text-muted-foreground">Personnes foyer</span><span className="font-medium">{sd.nbPersonnes}</span></div>}
            {sd.revenuFiscal && <div className="flex justify-between"><span className="text-muted-foreground">Revenu fiscal</span><span className="font-medium">{fc(sd.revenuFiscal)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Valorisation</span><span className="font-medium">{sd.valorisation || 0} EUR/MWh</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Mode cout</span><span className="font-medium">{sd.regieMode === "lead" ? "Lead" : "Regie"}</span></div>
          </div>
        </div>
      </div>

      {/* Multi-logement info */}
      {sd.multiLogement && (
        <div className="border border-border rounded-lg p-4 bg-amber-500/10">
          <h4 className="font-semibold text-sm mb-2 text-amber-700">Multi-logements : {sd.multiN || sd.nbLogements} logements</h4>
          {sd.multiLogementInfo && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {sd.multiLogementInfo.surfaceMode === "detail" && sd.multiLogementInfo.surfacesDetail?.length > 0 && (
                <div><span className="text-muted-foreground block mb-1">Surfaces detaillees</span>{sd.multiLogementInfo.surfacesDetail.map((s, i) => <span key={i} className="block">L{i+1}: {s}m2</span>)}</div>
              )}
              {sd.multiLogementInfo.chauffageMode === "detail" && sd.multiLogementInfo.chauffagesDetail?.length > 0 && (
                <div><span className="text-muted-foreground block mb-1">Chauffages detailles</span>{sd.multiLogementInfo.chauffagesDetail.map((c, i) => <span key={i} className="block">L{i+1}: {c}</span>)}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Eligibility */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {sd.elig174 && (
          <div className={`border rounded-lg p-3 ${sd.elig174.eligible ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {sd.elig174.eligible ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
              Programme 174
            </div>
            {sd.elig174.reason && <p className="text-xs text-muted-foreground mt-1">{sd.elig174.reason}</p>}
          </div>
        )}
        {sd.eligMPR && (
          <div className={`border rounded-lg p-3 ${sd.eligMPR.eligible ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {sd.eligMPR.eligible ? <CheckCircle2 className="w-4 h-4 text-blue-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
              Programme MPR
            </div>
            {sd.eligMPR.reason && <p className="text-xs text-muted-foreground mt-1">{sd.eligMPR.reason}</p>}
          </div>
        )}
        {sd.eligCumule && (
          <div className={`border rounded-lg p-3 ${sd.eligCumule.eligible ? 'bg-violet-500/10 border-violet-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {sd.eligCumule.eligible ? <CheckCircle2 className="w-4 h-4 text-violet-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
              Op. Cumulee
            </div>
            {sd.eligCumule.order && <p className="text-xs text-muted-foreground mt-1">Ordre: {sd.eligCumule.order.join(" puis ")}</p>}
          </div>
        )}
      </div>

      {/* Financial Results */}
      <div className="border border-border rounded-lg p-4">
        <h4 className="font-semibold text-sm mb-3 text-indigo-700">Resultats financiers</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {active174 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <p className="font-semibold text-emerald-700 mb-2">174 {isCum ? "(Cumulé)" : ""}</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">CEE</span><span className="font-medium">{fc((isCum ? active174Cum : active174)?.result?.cee)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">CA TTC</span><span className="font-medium">{fc((isCum ? active174Cum : active174)?.result?.caTTC)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cout travaux</span><span className="font-medium">{fc((isCum ? active174Cum : active174)?.result?.coutTravaux || (isCum ? active174Cum : active174)?.result?.coutTotal)}</span></div>
                {!isRestricted && <div className="flex justify-between"><span className="text-muted-foreground">Marge</span><span className="font-bold text-emerald-700">{fc((isCum ? active174Cum : active174)?.result?.marge)}</span></div>}
                {multiN > 1 && !isRestricted && <div className="flex justify-between"><span className="text-muted-foreground">Marge x{multiN}</span><span className="font-bold text-emerald-700">{fc(((isCum ? active174Cum : active174)?.result?.marge || 0) * multiN)}</span></div>}
              </div>
            </div>
          )}
          {activeMPR && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="font-semibold text-blue-700 mb-2">MPR ({sd.activeMPRType || "PAC"}) {isCum ? "(Cumulé)" : ""}</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Aide MPR</span><span className="font-medium">{fc((isCum ? activeMPRCum : activeMPR)?.result?.aideMPR || (isCum ? activeMPRCum : activeMPR)?.result?.mpr)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">CEE</span><span className="font-medium">{fc((isCum ? activeMPRCum : activeMPR)?.result?.cee)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total aides</span><span className="font-medium">{fc((isCum ? activeMPRCum : activeMPR)?.result?.totalAides)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cout total</span><span className="font-medium">{fc((isCum ? activeMPRCum : activeMPR)?.result?.coutTotal)}</span></div>
                {!isRestricted && <div className="flex justify-between"><span className="text-muted-foreground">Marge</span><span className="font-bold text-blue-700">{fc((isCum ? activeMPRCum : activeMPR)?.result?.marge)}</span></div>}
                {multiN > 1 && !isRestricted && <div className="flex justify-between"><span className="text-muted-foreground">Marge x{multiN}</span><span className="font-bold text-blue-700">{fc(((isCum ? activeMPRCum : activeMPR)?.result?.marge || 0) * multiN)}</span></div>}
              </div>
            </div>
          )}
        </div>

        {/* Cumule total */}
        {isCum && active174Cum && activeMPRCum && !isRestricted && (
          <div className="mt-3 bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 text-center">
            <p className="text-xs text-violet-600 font-semibold">Marge totale cumulee</p>
            <p className="text-2xl font-extrabold text-violet-700">{fc(((active174Cum.result?.marge || 0) + (activeMPRCum.result?.marge || 0)) * multiN)}</p>
          </div>
        )}

        {/* Visual Bar Chart */}
        {!isRestricted && (active174 || activeMPR) && (() => {
          const items = [];
          if (active174) {
            const r = isCum ? active174Cum?.result : active174.result;
            items.push({ label: "CEE 174", value: (r?.caTTC || r?.cee || 0) * multiN, color: "bg-emerald-400" });
            items.push({ label: "Coût 174", value: (r?.coutTravaux || r?.coutTotal || 0) * multiN, color: "bg-red-400" });
            items.push({ label: "Marge 174", value: (r?.marge || 0) * multiN, color: "bg-emerald-600" });
          }
          if (activeMPR) {
            const r = isCum ? activeMPRCum?.result : activeMPR.result;
            items.push({ label: "Aide MPR", value: (r?.aideMPR || r?.mpr || 0) * multiN, color: "bg-blue-400" });
            items.push({ label: "CEE MPR", value: (r?.cee || 0) * multiN, color: "bg-blue-300" });
            items.push({ label: "Coût MPR", value: (r?.coutTotal || 0) * multiN, color: "bg-red-300" });
            items.push({ label: "Marge MPR", value: (r?.marge || 0) * multiN, color: "bg-blue-600" });
          }
          const maxVal = Math.max(...items.map(i => Math.abs(i.value)), 1);
          return (
            <div className="mt-3 border border-border rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Banknote className="w-4 h-4 text-indigo-500" /> Visualisation financière</h4>
              <div className="space-y-2">
                {items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-right text-muted-foreground shrink-0">{item.label}</span>
                    <div className="flex-1 h-5 bg-muted/30 rounded-full overflow-hidden">
                      <div className={cn("h-full rounded-full transition-all", item.color, item.value < 0 ? "opacity-60" : "")}
                        style={{ width: `${Math.min((Math.abs(item.value) / maxVal) * 100, 100)}%` }} />
                    </div>
                    <span className={cn("w-20 font-medium", item.value < 0 ? "text-red-600" : "")}>{fc(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* 174 Scenarios Detail */}
      {results174.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <button onClick={() => setShow174Detail(!show174Detail)} className="flex items-center gap-2 text-sm font-semibold w-full text-left">
            {show174Detail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Scenarios 174 ({results174.length})
          </button>
          {show174Detail && (
            <div className="mt-3 space-y-2">
              {results174.map((r, i) => (
                <div key={i} className="bg-muted/30 rounded-lg p-3 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{r.scenario.label} ({r.scenario.sauts} sauts)</span>
                    {!isRestricted && <span className="font-bold text-emerald-700">{fc(r.result?.marge)}</span>}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {(r.scenario.gestes || []).map(g => GESTES_ISO_LABELS[g] || g).join(" + ")}
                    {(r.scenario.equipements || []).length > 0 && ` + ${r.scenario.equipements.map(e => COUT_UNITAIRE[e]?.label || e).join(", ")}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MPR Scenarios Detail */}
      {allMPRResults.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <button onClick={() => setShowMPRDetail(!showMPRDetail)} className="flex items-center gap-2 text-sm font-semibold w-full text-left">
            {showMPRDetail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Scenarios MPR ({allMPRResults.length})
          </button>
          {showMPRDetail && (
            <div className="mt-3 space-y-2">
              {allMPRResults.map((r, i) => (
                <div key={i} className="bg-muted/30 rounded-lg p-3 text-xs flex justify-between items-center">
                  <span className="font-semibold">{r.type}</span>
                  <div className="flex gap-4">
                    <span>Aides: {fc(r.result?.totalAides)}</span>
                    {!isRestricted && <span className="font-bold text-blue-700">Marge: {fc(r.result?.marge)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Travaux (gestes) detail */}
      {sd.autoQtes && Object.keys(sd.autoQtes).length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Hammer className="w-4 h-4 text-orange-500" /> Travaux recommandes</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(sd.autoQtes).filter(([,v]) => v > 0).map(([key, qty]) => {
              const cout = sd.couts?.[key] || {};
              return (
                <div key={key} className="flex items-center justify-between bg-muted/30 rounded-lg px-3 py-2 text-xs">
                  <span className="font-medium">{GESTES_ISO_LABELS[key] || COUT_UNITAIRE[key]?.label || key}</span>
                  <div className="flex gap-3">
                    <span>Qte: {qty}</span>
                    <span>Mat: {fc(cout.materiel * qty)}</span>
                    <span>Pose: {fc(cout.pose * qty)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline */}
      {timelines.timelines.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" /> Timeline estimee</h4>
          {renderTimelineInline(timelines.timelines, timelines.totalDays)}
        </div>
      )}

      {/* Cash Flow */}
      {timelines.cashEvents.length > 0 && !isRestricted && (
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Euro className="w-4 h-4 text-emerald-500" /> Cash-flow previsionnel</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 text-muted-foreground">Date</th>
                  <th className="text-left py-1.5 text-muted-foreground">Evenement</th>
                  <th className="text-right py-1.5 text-muted-foreground">Montant</th>
                  <th className="text-right py-1.5 text-muted-foreground">Solde</th>
                </tr>
              </thead>
              <tbody>
                {timelines.cashEvents.map((e, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-1.5">{e.dateStr}</td>
                    <td className="py-1.5">{e.label}</td>
                    <td className={cn("py-1.5 text-right font-medium", e.amount >= 0 ? "text-emerald-600" : "text-red-600")}>{e.amount >= 0 ? "+" : ""}{fc(e.amount)}</td>
                    <td className={cn("py-1.5 text-right font-bold", e.balance >= 0 ? "text-emerald-700" : "text-red-700")}>{fc(e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Programme selectionne */}
      {sd.programme_selectionne && (
        <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-500/10">
          <p className="text-sm font-semibold text-indigo-700">Programme selectionne pour ce dossier : <span className="uppercase">{sd.programme_selectionne}</span></p>
        </div>
      )}
    </div>
  );
}
